import React, { useState, useEffect } from "react";
import { ConfirmDeleteModal } from "../../../../../../Components/ConfirmDeleteModal";
import {
  TableColumnsType,
  Button,
  Space,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  Divider,
  message,
  Spin,
  Pagination,
  Select,
  Typography,
} from "antd";
import { Icon } from "@iconify/react";
import * as Yup from "yup";
import { Formik } from "formik";
import { debounce } from "lodash";

import { DataTable } from "../../../../../../Components/DataTable/DataTable";
import { panelApiUrl } from "~/Utils";
import { api, remove } from "../../../../../../../SPA/Services/api.service";
import { getFormattedDate, getKey } from "@utils/CommonUtils";
import { updatedAtOf, updatedByOf } from "../../../../../../Utils/auditFields";
import { apiSortField } from "../../../../../../Utils/sortField";
import { regionApi, toArray } from "../../../../../../Services";
import styles from "@styles/variables.module.scss";



export const CategoryList = () => {
  const [categoryData, setCategoryData] = useState<any[]>([]);
  /* The list URL, re-requested page by page by DataTable's Export (as VendorList). */
  const [categoryExportUrl, setCategoryExportUrl] = useState<string>("");
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
  const [deleteCategoryModal, setDeleteCategoryModal] = useState(false);
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [categoryTotal, setCategoryTotal] = useState<number>(0);
  const [categoryPage, setCategoryPage] = useState<number>(0);
  const [categorySearch, setCategorySearch] = useState<string>("");
  const [clearCategorySearch, setClearCategorySearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [existingCategories, setExistingCategories] = useState<any[]>([]);
  /* A category belongs to a region now (categoryCode + regionId are required
     on create): the form picks one, and the table can be filtered by one. */
  const [regions, setRegions] = useState<any[]>([]);
  const [regionFilter, setRegionFilter] = useState<string | undefined>(undefined);
  useEffect(() => {
    regionApi
      .getAll()
      .then((rows: any) =>
        setRegions(
          toArray(rows).sort((a: any, b: any) => String(a.regionName ?? "").localeCompare(String(b.regionName ?? "")))
        )
      )
      .catch(() => setRegions([]));
  }, []);

  useEffect(() => {
    if (openCategoryModal) {
      api
        .get(`${panelApiUrl}/category/get-category-list?page=0&size=1000`)
        .then((res: any) => {
          const records = res?.data?.response?.data?.records || [];
          setExistingCategories(records);
        })
        .catch(() => setExistingCategories([]));
    }
  }, [openCategoryModal]);

  const categorySchema = Yup.object().shape({
    categoryName: Yup.string()
      .required("Please enter a category")
      .min(2, "Category must be at least 2 characters long")
      .max(50, "Category must not exceed 50 characters")
      .trim("Leading and trailing spaces are not allowed")
      .strict(true)
      .test("unique", "This name already exists under the selected region.", function (value) {
        /* Unique per region — the same category can be set up in two regions,
           so there is nothing to clash with until a region is chosen. */
        const regionId = this.parent.regionId;
        if (!value || !regionId) return true;
        const isDuplicate = existingCategories.some(
          (c: any) =>
            c.categoryName?.toLowerCase() === value.toLowerCase() &&
            (!c.regionId || c.regionId === regionId) &&
            c.id !== editingCategory?.id
        );
        return !isDuplicate;
      }),
    categoryCode: Yup.string()
      .required("Please enter a category code")
      .trim("Leading and trailing spaces are not allowed")
      .strict(true)
      .matches(/^[A-Za-z0-9]{1,10}$/, "Letters and numbers only, up to 10 characters"),
    regionId: Yup.string().required("Please select a region"),
  });

  const recordsPerPage = 10;

  /* Runs on mount and whenever the region filter changes, from page 1. */
  useEffect(() => {
    setCategoryPage(0);
    fetchCategoryData(categorySearch, 0, sortOrder, orderBy);
    // eslint-disable-next-line
  }, [regionFilter]);

  const handleCategorySearchChange = debounce((value: string, isLoading: boolean) => {
    setClearCategorySearch(false);
    setCategorySearch(value);
    setCategoryPage(0);
    setCategoryLoading(isLoading);
    fetchCategoryData(value, 0, sortOrder, orderBy);
  }, 1000);

  const handleTableSort = (column: any, currentSortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (currentSortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (currentSortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      } else {
        setSortOrder("desc");
        order = "desc";
      }
    }
    setOrderBy(column.dataIndex);
    setCategoryPage(0);
    fetchCategoryData(categorySearch, 0, order, column.dataIndex);
  };

  const fetchCategoryData = (searchText: string = "", currentPage: number = 0, currentOrder: string = sortOrder, currentOrderBy: string = orderBy) => {
    setCategoryLoading(true);
    setCategoryError(null);

    const url = `${panelApiUrl}/category/get-category-list?page=${currentPage}&size=${recordsPerPage}&searchText=${encodeURIComponent(
      searchText
    )}&order=${currentOrder}&orderBy=${apiSortField(currentOrderBy)}${
      regionFilter ? `&regionId=${encodeURIComponent(regionFilter)}` : ""
    }`;
    setCategoryExportUrl(url);

    api
      .get(url)
      .then((res: any) => {
        const responseData = res?.data?.response?.data;
        const records = responseData?.records || [];
        setCategoryTotal(responseData?.totalCount || 0);

        const mappedData = records.map((item: any, index: any) => ({
          key: getKey(currentPage, index),
          id: item.id,
          categoryName: item.categoryName || "-",
          createdAt: getFormattedDate(item.createdOn) || "-",
          createdBy: item.createdBy || "-",
          updatedAt: updatedAtOf(item),
          updatedBy: updatedByOf(item),
          ...item,
        }));

        setCategoryData(mappedData);
      })
      .catch((err: any) => {
        console.error("Error fetching category data:", err);
        setCategoryData([]);
        setCategoryTotal(0);
        setCategoryError("Failed to fetch category data");
        message.error("Error fetching category data");
      })
      .finally(() => {
        setCategoryLoading(false);
      });
  };

  const openCategory = (record: any, mode: "edit" | "view") => {
    setEditingCategory(record);
    setModalMode(mode);
    setOpenCategoryModal(true);
  };

  const handleCategoryMenuClick = (key: string, record: { [key in string]: any }) => {
    if (key === "edit") {
      openCategory(record, "edit");
    } else if (key === "delete") {
      setDeletingCategoryId(record?.id);
      setDeleteCategoryModal(true);
    }
  };

  const handleDeleteCategory = () => {
    setDeleteCategoryLoading(true);
    remove(`${panelApiUrl}/category/${deletingCategoryId}`)
      .then((res: any) => {
        setDeleteCategoryLoading(false);
        message.success(res?.data?.message || "Category deleted successfully");
        setDeleteCategoryModal(false);
        setDeletingCategoryId(null);
        fetchCategoryData(categorySearch, categoryPage, sortOrder, orderBy);
      })
      .catch((err: any) => {
        setDeleteCategoryLoading(false);
        message.error(err?.data?.message || "Failed to delete category");
        setDeleteCategoryModal(false);
        setDeletingCategoryId(null);
      });
  };

  const handleCloseCategoryModal = () => {
    setOpenCategoryModal(false);
    setEditingCategory(null);
  };

  const categoryColumns: TableColumnsType = [
    { 
      title: "Category Name", 
      dataIndex: "categoryName", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "categoryName" ? "ascend" : sortOrder === "desc" && orderBy === "categoryName" ? "descend" : undefined,
    },
    {
      title: "Category Code",
      dataIndex: "categoryCode",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "categoryCode" ? "ascend" : sortOrder === "desc" && orderBy === "categoryCode" ? "descend" : undefined,
      render: (value: any) => value || "-",
    },
    {
      title: "Region",
      dataIndex: "regionName",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "regionName" ? "ascend" : sortOrder === "desc" && orderBy === "regionName" ? "descend" : undefined,
      render: (value: any) => value || "-",
    },
    { 
      title: "Created At", 
      dataIndex: "createdOn", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdOn" ? "ascend" : sortOrder === "desc" && orderBy === "createdOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    { 
      title: "Created By", 
      dataIndex: "createdBy", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdBy" ? "ascend" : sortOrder === "desc" && orderBy === "createdBy" ? "descend" : undefined,
    },
    { 
      title: "Updated At", 
      dataIndex: "updatedOn", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedOn" ? "ascend" : sortOrder === "desc" && orderBy === "updatedOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    { 
      title: "Updated By", 
      dataIndex: "updatedBy", 
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedBy" ? "ascend" : sortOrder === "desc" && orderBy === "updatedBy" ? "descend" : undefined,
    },
    {
      title: "Action",
      dataIndex: " ",
      width: 90,
      render: (value: any, record: any) => {
        const menuItems = [
          {
            label: (
              <Typography.Text
                type="secondary"
                style={{ color: styles?.primary }}
              >
                <Space>
                  <Icon icon="akar-icons:edit" inline fontSize={16} style={{ color: styles?.primary }} />
                  Edit
                </Space>
              </Typography.Text>
            ),
            key: "edit",
          },
          {
            label: (
              <Typography.Text
                type="danger"
              >
                <Space>
                  <Icon icon="fluent:delete-48-regular" inline fontSize={16} />
                  Delete
                </Space>
              </Typography.Text>
            ),
            key: "delete",
          },
        ];
        return (
          <Dropdown
            overlay={
              <Menu
                /* A portalled menu bubbles up the React tree, so the row
                   click handler on the cell would fire too. */
                onClick={({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  handleCategoryMenuClick(key, record);
                }}
                items={menuItems}
              />
            }
            trigger={["click"]}
            placement="bottomLeft"
          >
            <span onClick={(e) => e.stopPropagation()}>
              <Icon icon={"bi:three-dots-vertical"} style={{ cursor: "pointer" }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      {categoryLoading && categoryData.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : categoryError ? (
        <Typography.Text type="danger">{categoryError}</Typography.Text>
      ) : (
        <>
          <DataTable
            multiSelect={true}
            hideExport={false}
            /* The three-dots Action column is a menu, not data. */
            excludeExportCols={["Action"]}
            exportFileName="Category"
            exportApiUrl={categoryExportUrl}
            columns={categoryColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={categoryData}
            className="table-clickable"
            onClick={(record: any) => openCategory(record, "view")}
            searchPlaceholderText="Search by category name"
            searchQuery={categorySearch}
            clearStringSearch={clearCategorySearch}
            onSearchChange={handleCategorySearchChange}
            inputDisable={categoryLoading}
            isPagination={true}
            isServerSearchApi={true}
            totalRecords={categoryTotal}
            recordsPerPage={recordsPerPage}
            additionalCTAToRight={true}
            additionalCTA={
              <Space size={8}>
                {/* Same region filter as the Vendor list. */}
                <Select
                  placeholder="Region"
                  allowClear
                  style={{ width: 150 }}
                  value={regionFilter}
                  onChange={(value) => setRegionFilter(value)}
                >
                  {regions.map((region: any) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.regionName}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingCategory(null);
                    setModalMode("add");
                    setOpenCategoryModal(true);
                  }}
                >
                  <Space>
                    <Icon icon="akar-icons:plus" inline />
                    Add
                  </Space>
                </Button>
              </Space>
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={categoryPage + 1}
              total={categoryTotal}
              onChange={(newPage) => {
                const startIndex = newPage - 1;
                setCategoryPage(startIndex);
                fetchCategoryData(categorySearch, startIndex, sortOrder, orderBy);
              }}
              showSizeChanger={false}
              pageSize={10}
            />
          </div>
        </>
      )}

      {/* Add / Edit / View Category Modal */}
      <Modal
        title={modalMode === "view" ? "View Category" : editingCategory ? "Edit Category" : "Add Categories"}
        open={openCategoryModal}
        maskClosable={false}
        centered
        width="30%"
        onCancel={handleCloseCategoryModal}
        footer={null}
        destroyOnClose={true}
      >
        <Divider style={{ margin: "-20px 0 22px", borderTop: "1px solid #d9d9d9" }} />

        <Formik
          key={editingCategory?.id || "add"}
          enableReinitialize={true}
          initialValues={{
            categoryName: editingCategory?.categoryName || "",
            categoryCode: editingCategory?.categoryCode || "",
            regionId: editingCategory?.regionId || "",
          }}
          validationSchema={categorySchema}
          validateOnMount
          onSubmit={(formValues, { resetForm }) => {
            setIsLoading(true);
            /* The region's name travels with its id, as the API documents it. */
            const values = {
              ...formValues,
              categoryCode: formValues.categoryCode.toUpperCase(),
              regionName: regions.find((region: any) => region.id === formValues.regionId)?.regionName ?? "",
            };

            if (editingCategory) {
              api
                .put(`${panelApiUrl}/category/${editingCategory.id}`, values)
                .then((res: any) => {
                  message.success(res?.data?.message || "Category updated successfully");
                  setIsLoading(false);
                  resetForm();
                  handleCloseCategoryModal();
                  fetchCategoryData(categorySearch, categoryPage, sortOrder, orderBy);
                })
                .catch((err: any) => {
                  message.error(err?.data?.message || "Failed to update category");
                  setIsLoading(false);
                });
            } else {
              api
                .post(`${panelApiUrl}/category`, values)
                .then((res: any) => {
                  message.success(res?.data?.message);
                  setIsLoading(false);
                  resetForm();
                  handleCloseCategoryModal();
                  fetchCategoryData(categorySearch, categoryPage, sortOrder, orderBy);
                })
                .catch((err: any) => {
                  message.error(err?.data?.message || "Failed to add category");
                  setIsLoading(false);
                });
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isValid, dirty, setFieldValue, setFieldTouched }) => (
            <Form layout="vertical" onFinish={handleSubmit}>
              <Form.Item required
                label="Category"
                validateStatus={touched.categoryName && errors.categoryName ? "error" : ""}
                help={touched.categoryName && typeof errors.categoryName === "string" ? errors.categoryName : undefined}
              >
                <Input
                  name="categoryName"
                  value={values.categoryName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter category"
                  maxLength={50}
                  disabled={modalMode === "view"}
                />
              </Form.Item>

              <Form.Item required
                label="Category Code"
                validateStatus={touched.categoryCode && errors.categoryCode ? "error" : ""}
                help={touched.categoryCode && typeof errors.categoryCode === "string" ? errors.categoryCode : undefined}
              >
                <Input
                  name="categoryCode"
                  value={values.categoryCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. PW"
                  maxLength={10}
                  style={{ textTransform: "uppercase" }}
                  disabled={modalMode === "view"}
                />
              </Form.Item>

              <Form.Item required
                label="Region"
                validateStatus={touched.regionId && errors.regionId ? "error" : ""}
                help={touched.regionId && typeof errors.regionId === "string" ? errors.regionId : undefined}
              >
                <Select
                  placeholder="Select region"
                  value={values.regionId || undefined}
                  onChange={(value: string) => setFieldValue("regionId", value)}
                  onBlur={() => setFieldTouched("regionId", true)}
                  disabled={modalMode === "view"}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  showSearch
                  optionFilterProp="children"
                >
                  {regions.map((region: any) => (
                    <Select.Option
                      key={region.id}
                      value={region.id}
                    >
                      {region.regionName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, marginTop: 32 }}>
                <Button onClick={handleCloseCategoryModal} disabled={isLoading}>
                  {modalMode === "view" ? "Close" : "Cancel"}
                </Button>
                {modalMode !== "view" && (
                  <Button type="primary" htmlType="submit" disabled={!dirty || !isValid} loading={isLoading}>
                    {editingCategory ? "Update" : "Add"}
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Delete Category Modal */}
      <ConfirmDeleteModal
        open={deleteCategoryModal}
        message="Are you sure you want to delete the category?"
        isConfirming={deleteCategoryLoading}
        onCancel={() => {
          setDeleteCategoryModal(false);
          setDeletingCategoryId(null);
        }}
        onConfirm={handleDeleteCategory}
      />
    </>
  );
};
